import { type VariantProps } from 'class-variance-authority';
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import { forwardRef, HTMLAttributes, useState, useEffect, useRef } from 'react';
import { tableRowVariants } from '@/shared/constants/ui-variants';
import { cn } from '@/shared/lib/utils';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';
import type { ColumnDef, SortConfig } from '@/shared/types/table.types';
import { Button } from './button';

/**
 * Table component with mobile-responsive design
 * Converts to card layout on mobile devices
 */

// Base table wrapper with horizontal scroll indicator
const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showLeftIndicator, setShowLeftIndicator] = useState(false);
    const [showRightIndicator, setShowRightIndicator] = useState(false);

    useEffect(() => {
      const handleScroll = () => {
        if (scrollRef.current) {
          const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
          setShowLeftIndicator(scrollLeft > 0);
          setShowRightIndicator(scrollLeft + clientWidth < scrollWidth - 1);
        }
      };

      const scrollElement = scrollRef.current;
      if (scrollElement) {
        handleScroll(); // Initial check
        scrollElement.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', handleScroll);

        return () => {
          scrollElement.removeEventListener('scroll', handleScroll);
          window.removeEventListener('resize', handleScroll);
        };
      }

      return undefined;
    }, []);

    return (
      <div className="relative">
        {/* Left scroll indicator */}
        {showLeftIndicator && (
          <div className="pointer-events-none absolute left-0 top-0 z-10 flex h-full items-center bg-gradient-to-r from-background to-transparent pl-2">
            <ChevronLeft
              className="size-6 text-muted-foreground"
              aria-hidden="true"
            />
          </div>
        )}

        {/* Right scroll indicator */}
        {showRightIndicator && (
          <div className="pointer-events-none absolute right-0 top-0 z-10 flex h-full items-center bg-gradient-to-l from-background to-transparent pr-2">
            <ChevronRight
              className="size-6 text-muted-foreground"
              aria-hidden="true"
            />
          </div>
        )}

        {/* Scrollable table container */}
        <div ref={scrollRef} className="w-full overflow-auto">
          {/* 
            Table structure (thead, tbody, etc.) is provided by consumers using 
            TableHeader, TableBody, TableRow, etc. components. This is a wrapper component.
          */}
          <table
            ref={ref}
            className={cn('w-full caption-bottom text-sm', className)}
            {...props}
          />
        </div>
      </div>
    );
  }
);
Table.displayName = 'Table';

// Table header
const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn('border-b border-border', className)}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

// Table body
const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('', className)} {...props} />
));
TableBody.displayName = 'TableBody';

// Table footer
const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t border-border bg-muted font-medium', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

// Table row
export interface TableRowProps
  extends
    HTMLAttributes<HTMLTableRowElement>,
    VariantProps<typeof tableRowVariants> {}

const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, density, hoverable, clickable, striped, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        tableRowVariants({ density, hoverable, clickable, striped }),
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

// Table head cell
const TableHead = forwardRef<
  HTMLTableCellElement,
  HTMLAttributes<HTMLTableCellElement> & {
    readonly sortable?: boolean | undefined;
    readonly sorted?: 'asc' | 'desc' | null | undefined;
    readonly onSort?: (() => void) | undefined;
    readonly align?: 'left' | 'center' | 'right' | undefined;
  }
>(
  (
    { className, children, sortable, sorted, onSort, align = 'left', ...props },
    ref
  ) => {
    const getSortIcon = () => {
      if (!sortable) return null;

      if (sorted === 'asc') {
        return <ChevronUp className="size-4" aria-hidden="true" />;
      }
      if (sorted === 'desc') {
        return <ChevronDown className="size-4" aria-hidden="true" />;
      }
      return <ChevronsUpDown className="size-4" aria-hidden="true" />;
    };

    const columnLabel = typeof children === 'string' ? children : 'column';
    const sortStatus = sorted || 'not sorted';

    const content = sortable ? (
      <Button
        type="button"
        variant={ButtonVariant.Ghost}
        size={ButtonSize.Small}
        onClick={onSort}
        className="h-auto p-0 font-medium hover:bg-transparent hover:text-foreground"
        aria-label={`Sort by ${columnLabel}, currently ${sortStatus}`}
      >
        {children}
        {getSortIcon()}
      </Button>
    ) : (
      children
    );

    return (
      <th
        ref={ref}
        className={cn(
          'h-12 px-4 text-left align-middle font-medium text-muted-foreground',
          align === 'center' && 'text-center',
          align === 'right' && 'text-right',
          className
        )}
        {...props}
      >
        {content}
      </th>
    );
  }
);
TableHead.displayName = 'TableHead';

// Table cell
const TableCell = forwardRef<
  HTMLTableCellElement,
  HTMLAttributes<HTMLTableCellElement> & {
    readonly align?: 'left' | 'center' | 'right' | undefined;
  }
>(({ className, align = 'left', ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'p-4 align-middle',
      align === 'center' && 'text-center',
      align === 'right' && 'text-right',
      className
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

// Table caption
const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('mt-4 text-sm text-muted-foreground', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

/**
 * Helper functions for MobileCard component
 */
const createCardClickHandler = <TData,>(
  onRowClick: ((row: TData) => void) | undefined,
  row: TData
) => (onRowClick ? () => onRowClick(row) : undefined);

const createCardKeyDownHandler = <TData,>(
  onRowClick: ((row: TData) => void) | undefined,
  row: TData
) =>
  onRowClick
    ? (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onRowClick(row);
        }
      }
    : undefined;

const getCardClassName = (onRowClick: boolean, className?: string) =>
  cn(
    'rounded-lg border border-border bg-card shadow-sm transition-all',
    onRowClick && 'cursor-pointer active:scale-[0.98]',
    onRowClick && 'hover:shadow-md hover:border-accent',
    className
  );

const renderExpandButton = <TData,>(
  columns: readonly ColumnDef<TData>[],
  isExpanded: boolean,
  onToggleExpand: (key: string) => void,
  rowKey: string,
  index: number
) => {
  if (columns.length <= 3) return null;

  return (
    <div className="border-t border-border bg-muted/30 px-4 py-2">
      <Button
        type="button"
        variant={ButtonVariant.Ghost}
        size={ButtonSize.Small}
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand(rowKey);
        }}
        className="h-11 min-h-[44px] w-full justify-center text-xs font-medium hover:bg-accent"
        aria-expanded={isExpanded}
        aria-label={
          isExpanded
            ? `Collapse details for row ${index + 1}`
            : `Expand details for row ${index + 1}`
        }
      >
        {isExpanded ? (
          <>
            <ChevronUp className="mr-2 size-4" aria-hidden="true" />
            Show less
          </>
        ) : (
          <>
            <ChevronDown className="mr-2 size-4" aria-hidden="true" />
            Show more ({columns.length - 3} more fields)
          </>
        )}
      </Button>
    </div>
  );
};

/**
 * Mobile card component for responsive table display
 * Displays table rows as cards on mobile devices
 */
interface MobileCardProps<TData> {
  readonly row: TData;
  readonly index: number;
  readonly rowKey: string;
  readonly columns: readonly ColumnDef<TData>[];
  readonly isExpanded: boolean;
  readonly onToggleExpand: (key: string) => void;
  readonly onRowClick?: ((row: TData) => void) | undefined;
  readonly className?: string | undefined;
}

function MobileCard<TData>({
  row,
  index,
  rowKey,
  columns,
  isExpanded,
  onToggleExpand,
  onRowClick,
  className,
}: MobileCardProps<TData>) {
  const visibleColumns = isExpanded ? columns : columns.slice(0, 3);
  const handleCardClick = createCardClickHandler(onRowClick, row);
  const handleKeyDown = createCardKeyDownHandler(onRowClick, row);

  return (
    <div
      className={getCardClassName(!!onRowClick, className)}
      onClick={handleCardClick}
      role={onRowClick ? 'button' : 'listitem'}
      {...(onRowClick && { tabIndex: 0 })}
      onKeyDown={handleKeyDown}
    >
      <div className="space-y-3 p-4">
        {visibleColumns.map((column, colIndex) => (
          <div
            key={column.id}
            className={cn(
              'flex flex-col gap-1',
              colIndex === 0 && 'pb-2 border-b border-border'
            )}
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {column.header}
            </div>
            <div
              className={cn(
                'text-sm text-foreground',
                colIndex === 0 && 'text-base font-medium'
              )}
            >
              {column.cell
                ? column.cell(row)
                : String(row[column.accessorKey] ?? '-')}
            </div>
          </div>
        ))}
      </div>

      {renderExpandButton(columns, isExpanded, onToggleExpand, rowKey, index)}
    </div>
  );
}

/**
 * Mobile-responsive data table component
 * Automatically switches to card layout on mobile devices
 */
interface DataTableProps<TData> {
  readonly data: readonly TData[];
  readonly columns: readonly ColumnDef<TData>[];
  readonly sortConfig?: SortConfig<TData> | null;
  readonly onSort?: (key: keyof TData) => void;
  readonly onRowClick?: (row: TData) => void;
  readonly getRowKey?: (row: TData, index: number) => string;
  readonly loading?: boolean;
  readonly emptyMessage?: string;
  readonly striped?: boolean;
  readonly hoverable?: boolean;
  readonly compact?: boolean;
  readonly mobileBreakpoint?: 'sm' | 'md' | 'lg';
  readonly className?: string;
}

function DataTable<TData>({
  data,
  columns,
  sortConfig,
  onSort,
  onRowClick,
  getRowKey,
  loading = false,
  emptyMessage = 'No data available',
  striped = false,
  hoverable = true,
  compact = false,
  mobileBreakpoint = 'lg',
  className,
}: DataTableProps<TData>) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRowExpansion = (key: string) => {
    setExpandedRows((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Default row key generator
  const defaultGetRowKey = (row: TData, index: number): string => {
    // Try to use 'id' property if it exists
    if (row && typeof row === 'object' && 'id' in row) {
      return String(row.id);
    }
    return `row-${index}`;
  };

  const rowKeyGetter = getRowKey ?? defaultGetRowKey;

  const breakpointClass = {
    sm: 'sm:table',
    md: 'md:table',
    lg: 'lg:table',
  }[mobileBreakpoint];

  if (loading) {
    return (
      <div
        className="flex h-64 items-center justify-center"
        role="status"
        aria-live="polite"
      >
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div
        className="flex h-64 items-center justify-center text-muted-foreground"
        role="status"
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className={cn('hidden w-full', breakpointClass)}>
        <Table className={className}>
          <TableHeader>
            <TableRow hoverable={false}>
              {columns.map((column) => (
                <TableHead
                  key={column.id}
                  sortable={column.sortable}
                  sorted={
                    sortConfig?.key === column.accessorKey
                      ? sortConfig.direction
                      : null
                  }
                  onSort={
                    column.sortable && onSort
                      ? () => onSort(column.accessorKey)
                      : undefined
                  }
                  align={column.align}
                  className={column.headerClassName}
                  style={{
                    width: column.width,
                    minWidth: column.minWidth,
                  }}
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, index) => {
              const rowKey = rowKeyGetter(row, index);
              return (
                <TableRow
                  key={rowKey}
                  hoverable={hoverable}
                  clickable={!!onRowClick}
                  onClick={() => onRowClick?.(row)}
                  className={cn(
                    striped && index % 2 === 1 && 'bg-muted/30',
                    compact && 'h-10'
                  )}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align ?? 'left'}
                      className={column.className}
                    >
                      {column.cell
                        ? column.cell(row)
                        : String(row[column.accessorKey] ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div
        className={cn('space-y-3', breakpointClass.replace('table', 'hidden'))}
        role="list"
        aria-label="Data table in card view"
      >
        {data.map((row, index) => {
          const rowKey = rowKeyGetter(row, index);
          const isExpanded = expandedRows.has(rowKey);

          return (
            <MobileCard
              key={rowKey}
              row={row}
              index={index}
              rowKey={rowKey}
              columns={columns}
              isExpanded={isExpanded}
              onToggleExpand={toggleRowExpansion}
              onRowClick={onRowClick}
              className={className}
            />
          );
        })}
      </div>
    </>
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableRow,
  TableHead,
  TableCell,
  TableCaption,
  DataTable,
};

export { TableDensity, TableRowVariant } from '@/shared/types/table.types';
